import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { AncillaryConnection } from "../../types/ancillary";
import { Work } from "../../types/work";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import useAncillaryQuery from "../../hooks/query/connections/useAncillaryQuery";
import useAncillaryLogic from "../../hooks/logic/connections/ancillaryLogic";

interface AncillaryController {
  from?: Work | null;
  to?: Work | null;
}

const useAncillaryConnectionController = ({
  from = null,
  to = null,
}: AncillaryController): ConnectionFormDataController<
  AncillaryConnection,
  Work,
  Work
> => {
  if ((from === null && to === null) || (from && to)) {
    throw new Error("Exactly one of `from` or `to` must be provided");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity = (from || to)!;
  const { useFetchById, useUpdate, useCreate } = useAncillaryQuery();
  const { defaultAncillary, metadataFieldNames, userEnums } = useAncillaryLogic(
    from,
    to
  );
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const [isDialogShown, setShowDialog] = useState(false);

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (edgeId: string) => {
    setId(edgeId);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<AncillaryConnection> => {
    if (id === null) return defaultAncillary;
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Ancillary connection not found");
    }
    return connectionData;
  };

  const onSubmit = async (data: Partial<AncillaryConnection>) => {
    const metadata: Partial<Omit<AncillaryConnection, "_from" | "_to">> = {
      notes: data.notes,
      work_ancillary_type: data.work_ancillary_type,
      internal_notes: data.internal_notes,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      attributes_metadata: {
        ...defaultAncillary.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    const updateData: CreateEdgeData<AncillaryConnection> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Team Member Relation");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Team Member Relation");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;

  const title = () => `${id ? "Edit" : "Create New"} Ancillary Relation`;

  return {
    defaultValues: defaultAncillary,
    metadataFieldNames,
    isCreateMode,
    userEnums,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};

export default useAncillaryConnectionController;
